import VMasker from 'vanilla-masker';
import useToastContext from "@byma/shared/hooks/useToastContext";
import SearchUser from './SearchUser'
import React, { Fragment, useState, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import { number as yupNumber, array as yupArray, object as yupObject, string as yupString } from 'yup'
import fetcher from '@byma/shared/helpers/fetcher'
import useAuth from '@byma/shared/hooks/useAuth'
import useForm from '@byma/shared/hooks/useForm'
import useSWR from 'swr'
import Input from '@byma/shared/components/Input'
import SearchAddressInput from '@byma/shared/components/SearchAddressInput'
import { Editor } from '@tinymce/tinymce-react';
import { FiAlertCircle, FiChevronDown, FiCheck } from 'react-icons/fi'
import { Combobox, Transition } from '@headlessui/react'
import Fuse from 'fuse.js'
import CloudinaryUpload from '@byma/shared/components/CloudinaryUpload'
import Loader from '@byma/shared/components/Loader';
import NewEvent from 'pages/events/new';
import FormSection from '@byma/shared/components/FormSection'
import GatewaySessionSelector from 'components/GatewaySessionSelector'

/*
async function imagesUploadHandler(blobInfo, success, failure, progress) {
    const formData = new FormData()
    formData.append('file', blobInfo.blob(), blobInfo.filename());
    formData.append('upload_preset', 'd9fee5bb')

    try {
        const req = await fetch('https://api.cloudinary.com/v1_1/htkavmx5a/image/upload', {
            method: 'POST',
            body: formData
        })
        const img = await req.json()
        success(img.secure_url)
    } catch (e) {
        console.log(e)
        failure('Erro ao realizar upload do arquivo')
    }
}
*/

function ColorPicker({ title, setFieldValue, values, colorName }) {
    const [displayColorPicker, setDisplayColorPicker] = React.useState(false)
    const color = values && values.settings && values.settings.colors && values.settings.colors.global && values.settings.colors.global[colorName]
    return (
        <div>
            <div className="border-1 border-solid rounded-lg p-6 flex flex-row justify-between items-center mb-6 " onClick={() => setDisplayColorPicker(true)}>
                <div>{title}</div>
                <div className={`border-3 border-solid  ${color === 'default' ? 'bg-gradient-to-r ' : 'bg-color-red'}`} />
            </div>
            { displayColorPicker ? <div className="absolute z-20">
                <div className="fixed inset-0 " onClick={() => setDisplayColorPicker(false)} />
                <SketchPicker
                    color={color === 'default' ? '#FF5C00' : color}
                    onChangeComplete={value => setFieldValue(`settings.colors.global.${colorName}`, value.hex)}
                />
            </div>
            : null}
        </div>
    )
}

export default function EventForm({ event = null, onSubmit, isLoading }) {
    const session = useAuth()
    const { token } = session

    const addToast = useToastContext()
    const { data: partners, error } = useSWR(token ? ['/api/partners/getAll?levels=ADMIN&onlyActive=true', null, token] : null, fetcher)
    const [query, setQuery] = useState('')
    const comboButtonRef = useRef(null)

    const filteredPartners = useMemo(() => {
        if (!partners?.result) return []
        if (query === '') return partners.result

        const fuse = new Fuse(partners.result, {
            keys: ['name'],
            threshold: 0.3
        })

        return fuse.search(query).map(result => result.item)
    }, [partners, query])

    const { data: formData, setFieldValue, errors: formErrors, handleSubmit, resetErrors: resetFormErrors, getError } = useForm({
        initialValues: event ? {
            ...event,
            date: dayjs(event.date).format('YYYY-MM-DDTHH:mm:ss'),
            description: event.description.replaceAll('<br />', '\r\n')
        } : {
            name: '',
            listed: false,
            partnerId: '',
            description: '',
            pictures: { photos: '' },
            date: '',
            location: { name: '', address: '', lat: '', lng: '' },
            maxTicketsPerOrder: '5',
            settings: Object.assign({
                colors: {
                    global: {
                        primary: '#F82525',
                        backgroundColor: 'white',
                        color: 'black',
                        button: 'default',
                    }
                },
                media: { backgroundImage: '' }
            }, {}),
            gatewaySessionOption: 'create',
        },
        schema: yupObject().shape({
            name: yupString()
            .required('Campo obrigatório'),
            partnerId: yupString()
            .required('Selecione uma opção'),
            maxTicketsPerOrder: yupNumber()
            .required('Campo obrigatório')
            .test('check-valor', 'Insira um valor maior que 0 e menor ou igual à 10', value => value > 0 && value <= 10),
            date: yupString()
            .required('Campo obrigatório')
            .test('check-date', 'Data inválida', checkDate),
            location: yupObject().shape({
                name: yupString()
                .required('Campo obrigatório'),
                address: yupString()
                .required('Campo obrigatório'),
            }),
            gatewaySessionOption: yupString()
            .test('gateway-selected', 'Selecione uma opção', value => event?._id || value),
        }),
        onSubmit: handleSave
    })

    React.useEffect(() => {
        if (formData.name) {
            setFieldValue('gatewaySessionName', `Sessão de ${formData.name}`)
        }
    }, [formData.name])

    function checkDate(formikDate) {
        return dayjs(formikDate).isAfter(dayjs())
    }

    function renderError(key) {
        const error = getError(key)
        return error && <div className="error">{error}</div>
    }
    async function handleSave(values) {
        const requestBody = ({
            partnerId: values.partnerId,
            name: values.name,
            maxTicketsPerOrder: values.maxTicketsPerOrder,
            pictures: values.pictures,
            listed: values.listed,
            location: values.location,
            settings: values.settings,
            description: values.description.replace(/\r?\n/g, '<br />'),
            date: dayjs(values.date).toISOString(),
            gatewaySessionOption: values.gatewaySessionOption,
        })

        if (values.gatewaySessionOption === 'create') {
            requestBody.gatewaySessionName = values.gatewaySessionName 
            if (!values.gatewaySessionName) {
                return addToast('Escolha um nome para a sessão de portaria', 'error')
            }
        } else if (values.gatewaySessionOption === 'addToExisting') {
            if (!values.selectedGatewaySessions || values.selectedGatewaySessions.length === 0) {
                return addToast('Selecione uma sessão de portaria', 'error')
            }
            requestBody.gatewaySessionIds = values.selectedGatewaySessions
        }

        return onSubmit(requestBody)
    }

    function handleAddress(geocoded, original) {
        setFieldValue('location.lat', geocoded.geometry.location.lat())
        setFieldValue('location.lng', geocoded.geometry.location.lng())
        setFieldValue('location.address', geocoded.formatted_address)
        setFieldValue('location.name', original?.structured_formatting?.main_text || original?.description)
    }

    if (!partners) {
        return <Loader />

    }

    return (
        <div className='flex flex-col space-y-6'>

            {!formData._id && (
                <>

                    <FormSection
                        title="Equipe"
                        infos={
                            <div className="flex flex-col space-y-3">
                                <p className="text-gray-800">
                                    Defina uma equipe para acompanhar os dados de venda deste evento.
                                </p>
                                <p className="text-gray-800">
                                    Esta informação definirá tanto quem tem acesso ao evento como as taxas de contrato.
                                </p>
                            </div>
                        }
                    >
                        {partners ?
                            <div className="flex flex-col mb-3">
                                <Combobox
                                    value={formData.partnerId}
                                    onChange={(value) => setFieldValue('partnerId', value)}
                                >
                                    {({ open }) => (
                                        <div className="relative">
                                            <div className={`relative w-full cursor-default overflow-hidden rounded border bg-white text-left focus-within:border-blue-500 ${renderError('partnerId') ? 'border-red-500' : 'border-gray-300'}`}>
                                                <Combobox.Input
                                                    className="w-full border-none p-4 text-lg leading-5 text-gray-900 focus:ring-0 outline-none"
                                                    displayValue={(partnerId) => {
                                                        const partner = partners.result.find(p => p._id === (partnerId?._id || partnerId))
                                                        return partner?.name || ''
                                                    }}
                                                    onChange={(event) => setQuery(event.target.value)}
                                                    onFocus={() => !open && comboButtonRef.current?.click()}
                                                    placeholder="Selecione uma equipe"
                                                />
                                                <Combobox.Button ref={comboButtonRef} className="absolute inset-y-0 right-0 flex items-center pr-2">
                                                    <FiChevronDown
                                                        className="h-5 w-5 text-gray-400"
                                                        aria-hidden="true"
                                                    />
                                                </Combobox.Button>
                                            </div>
                                            <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                                afterLeave={() => setQuery('')}
                                            >
                                                <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                    {filteredPartners.length === 0 && query !== '' ? (
                                                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                                            Nenhuma equipe com contrato ativo disponível
                                                        </div>
                                                    ) : (
                                                        filteredPartners.map((partner) => (
                                                            <Combobox.Option
                                                                key={partner._id}
                                                                className={({ active }) =>
                                                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-900'
                                                                    }`
                                                                }
                                                                value={partner._id}
                                                            >
                                                                {({ selected, active }) => (
                                                                    <>
                                                                        <span
                                                                            className={`block truncate ${
                                                                                selected ? 'font-medium' : 'font-normal'
                                                                            }`}
                                                                        >
                                                                            {partner.name}
                                                                        </span>
                                                                        {selected ? (
                                                                            <span
                                                                                className={`absolute inset-y-0 left-0 flex items-center pl-3 text-primary`}
                                                                            >
                                                                                <FiCheck className="h-5 w-5" aria-hidden="true" />
                                                                            </span>
                                                                        ) : null}
                                                                    </>
                                                                )}
                                                            </Combobox.Option>
                                                        ))
                                                    )}
                                                </Combobox.Options>
                                            </Transition>
                                        </div>
                                    )}
                                </Combobox>
                                <div className="flex text-xs text-red-500">
                                    {renderError('partnerId')}
                                </div>
                            </div>
                            :
                            <Loader />
                        }
                    </FormSection>
                </>
            )}


            <FormSection
                title="Informações básicas"
                infos={
                    <div className="flex flex-col space-y-3">
                        <p className="text-gray-800">
                            Estas informa&ccedil;&otilde;es aparecer&atilde;o na página do evento, nos ingressos e em toda comunica&ccedil;&atilde;o.
                        </p>
                        <p className="text-gray-800">
                            <strong>Nome:</strong> Insira o nome do evento, caso necessário, será possível edita-lo.
                        </p>
                        <p className="text-gray-800">
                            <strong>Data e hora de início:</strong>  Insira a data e horário em que evento ocorrerá. Será considerado o Horário de Brasília (GMT-3) 
                        </p>
                    </div>
                }
            >
                <Input
                    error={renderError('name')}
                    label={'Nome do evento'}
                    value={formData.name}
                    onChange={(event) => setFieldValue(`name`, event.target.value)}
                />
                <Input
                    error={renderError('date')}
                    label={'Data e hora de início'}
                    value={formData.date}
                    onChange={(event) => setFieldValue(`date`, event.target.value)}
                    type="datetime-local"
                />
            </FormSection>
            <FormSection
                title="Localização"
                infos={
                    <div className="flex flex-col space-y-3">
                        <p className="text-gray-800">
                            Utilize o campo de busca para localizar e definir o local do seu evento.
                        </p>
                        <p className="text-gray-800">
                            Esta informação ficará visível na página de compra.
                        </p>
                    </div>
                }
            >
                <div className="mb-6">
                    <SearchAddressInput onChange={handleAddress}/>
                </div>
                <div className="flex flex-col md:flex-row md:space-x-3 mb-3">
                    <Input
                        error={renderError('location.name')}
                        label={'Nome do local'}
                        value={formData.location.name}
                        onChange={(event) => setFieldValue(`location.name`, event.target.value)}
                    />
                    <Input
                        error={renderError('location.address')}
                        label={'Endereço do local'}
                        value={formData.location.address}
                        onChange={(event) => setFieldValue(`location.address`, event.target.value)}
                    />
                </div>
                <div className="flex flex-col md:flex-row md:space-x-3 mb-3">
                    <Input
                        error={renderError('location.lat')}
                        label={'Latitude'}
                        value={formData.location.lat}
                        onChange={(event) => setFieldValue(`location.lat`, event.target.value)}
                        inputMode={'numeric'}
                        type="number"
                    />
                    <Input
                        error={renderError('location.lng')}
                        label={'Longitude'}
                        value={formData.location.lng}
                        onChange={(event) => setFieldValue(`location.lng`, event.target.value)}
                        inputMode={'numeric'}
                        type="number"
                    />
                </div>
            </FormSection>


            <FormSection
                title="Opções de venda"
                infos={
                    <div className="flex flex-col space-y-3">
                        <p className="text-gray-800">
                            Defina se este evento estará listado nas plataformas Byma (evento público) ou se permanecerá oculto (acessível apenas pelo link de venda).
                        </p>
                        <p className="text-gray-800">
                            Você pode definir qual o limite máximo de convites que uma pessoa pode selecionar em uma compra. (Mínimo: 1 - Máximo: 5)
                        </p>
                    </div>
                }
            >
                <div className="w-full border shadow-sm box-hover rounded p-4 flex items-center space-x-3 cursor-pointer" onClick={() => setFieldValue('listed', !formData.listed)}>
                    <input type="checkbox" checked={formData.listed} className="p-0 m-0" />
                    <span>Evento publico</span>
                </div>
                <Input
                    error={renderError('maxTicketsPerOrder')}
                    label={'Máximo por Compra'}
                    inputProps={{ inputMode: 'numeric' }}
                    value={formData.maxTicketsPerOrder}
                    onChange={(event) => setFieldValue(`maxTicketsPerOrder`, VMasker.toNumber(event.target.value))}
                />
            </FormSection>

            <FormSection
                title="Imagem de capa"
                infos={
                    <>
                        <p className="text-gray-800">
                            A dimensão ideal para a imagem de capa é de 1440x500 pixels.
                        </p>
                    </>
                }
            >
                <CloudinaryUpload onChange={(value) => setFieldValue('pictures.photos', [value])} />
                {formData?.pictures?.photos?.length > 0 && (
                    <img src={formData.pictures.photos[0]} className="w-full h-48 object-cover" alt="Capa do evento" />
                )}
            </FormSection>
            <FormSection
                title="Breve descrição"
                infos={
                    <>
                        <p className="text-gray-800">
                            Insira um resumo do evento. Esta descrição ficará visível em sites de busca e junto ao link do evento, quando compartilhado.
                        </p>
                    </>
                }
            >
                <textarea
                    className="shadow-sm border rounded focus:border-blue-500 outline-none p-4"
                    value={formData.description}
                    onChange={(event) => setFieldValue(`description`, event.target.value)}
                />
                <div className="flex text-xs text-red-500">
                    {renderError('name')}
                </div>
            </FormSection>
            { !event?._id && (
                <FormSection
                    title="Sessão de portaria"
                    infos={
                        <>
                            <p className="text-gray-800">
                                Escolha entre criar uma nova sessão para validação dos ingressos deste evento, utilizar uma sessão já existente ou não utilizar sessão de portaria por enquanto.
                            </p>
                        </>
                    }
                >

                    <div className="w-full border shadow-sm box-hover rounded p-4 flex items-center space-x-3 cursor-pointer" onClick={() => setFieldValue('gatewaySessionOption', 'create')}>
                        <input type="checkbox" checked={formData.gatewaySessionOption === 'create'} className="p-0 m-0" />
                        <span>Criar sessão</span>
                    </div>
                    <div className="w-full border shadow-sm box-hover rounded p-4 flex items-center space-x-3 cursor-pointer" onClick={() => setFieldValue('gatewaySessionOption', 'addToExisting')}>
                        <input type="checkbox" checked={formData.gatewaySessionOption === 'addToExisting'} className="p-0 m-0" />
                        <span>Adicionar à sessão</span>
                    </div>
                    <div className="w-full border shadow-sm box-hover rounded p-4 flex items-center space-x-3 cursor-pointer" onClick={() => setFieldValue('gatewaySessionOption', 'none')}>
                        <input type="checkbox" checked={formData.gatewaySessionOption === 'none'} className="p-0 m-0" />
                        <span>Este evento não terá validação de ingressos</span>
                    </div>
                    { formData.gatewaySessionOption === 'create' && (
                        <Input
                            error={renderError('gatewaySessionName')}
                            label={'Nome da sessão'}
                            value={formData.gatewaySessionName}
                            onChange={(event) => setFieldValue(`gatewaySessionName`, event.target.value)}
                        />
                    )}
                    { formData.gatewaySessionOption === 'addToExisting' && (
                        <GatewaySessionSelector 
                            selectedGatewaySessions={formData.selectedGatewaySessions} 
                            onChange={(gatewaySessionIds) => setFieldValue('selectedGatewaySessions', gatewaySessionIds)} 
                        />
                    )}
                    <div className="flex text-xs text-red-500">
                        {renderError('gatewaySessionOption')}
                    </div>
                </FormSection>
            )}

            <div className='mt-5 w-full flex justify-center md:justify-end'>
                {isLoading ? (
                    <div>
                        <Loader />
                    </div>
                ) : (
                    <button className="btn btn-primary w-full" onClick={handleSubmit} disabled={isLoading}>
                        Salvar
                    </button>
                )}
            </div>
        </div>
    )
}
